// Pack-size selection for the water-test shopping list.
// A chemistry product can carry stocked pack sizes (e.g. BP100 2kg / 4kg / 10kg) linked to Parts.
// Given the dose the engine calculated, pick the pack(s) that cover it with least waste:
//   - if a single pack covers it -> the SMALLEST single pack that covers it
//   - otherwise fill largest-first, then top the remainder with the smallest pack that covers it
// We never leave the client short (always round up to cover the required amount).

// convert a dose amount into the pack's unit (g<->kg, mls<->L)
function toPackUnit(amount, fromUnit, packUnit) {
  const f = (fromUnit || "").toLowerCase(), p = (packUnit || "").toLowerCase();
  if (f === p) return amount;
  if (f === "g" && p === "kg") return amount / 1000;
  if (f === "kg" && p === "g") return amount * 1000;
  if (f === "mls" && p === "l") return amount / 1000;
  if (f === "ml" && p === "l") return amount / 1000;
  if (f === "l" && (p === "mls" || p === "ml")) return amount * 1000;
  return amount; // unknown pairing — assume same
}

// packs: [{ part_id, label, size, unit, price }]
export function selectPacks(amount, unit, packs) {
  const clean = (packs || []).filter((p) => Number(p.size) > 0).map((p) => ({ ...p, size: Number(p.size), price: Number(p.price) || 0 }));
  if (!clean.length || !(amount > 0)) return null;
  const packUnit = clean[0].unit || "kg";
  let need = toPackUnit(amount, unit, packUnit);
  const bySizeAsc = [...clean].sort((a, b) => a.size - b.size);
  const bySizeDesc = [...clean].sort((a, b) => b.size - a.size);
  const biggest = bySizeDesc[0];

  const chosen = {}; // part_id -> {pack, qty}
  const add = (pack, qty = 1) => {
    if (!chosen[pack.part_id]) chosen[pack.part_id] = { pack, qty: 0 };
    chosen[pack.part_id].qty += qty;
  };

  // use as many full "biggest" packs as fit (leaving a remainder we can still cover)
  while (need > biggest.size + 1e-9) { add(biggest); need -= biggest.size; }
  // cover the remainder with the smallest single pack that covers it
  if (need > 1e-9) {
    const cover = bySizeAsc.find((p) => p.size >= need - 1e-9) || biggest;
    add(cover);
  }
  return Object.values(chosen).map(({ pack, qty }) => ({
    name: pack.label || "", part_id: pack.part_id, qty, size: pack.size, unit: packUnit,
    price: pack.price, total: Math.round(pack.price * qty * 100) / 100,
  }));
}

// For a given recommendation + the active brand range (already narrowed to the category family),
// return pack line items if the recommended product has pack sizes, else null.
export function packsForRec(rec, matrixForCat) {
  if (!rec || !matrixForCat) return null;
  const list = matrixForCat[rec.action] || [];
  const want = String(rec.product || "").toLowerCase();
  const prod = Object.values(matrixForCat).flat().find((p) => p && String(p.name || "").toLowerCase() === want) || (want ? null : list[0]);
  if (!prod || !prod.packs || !prod.packs.length) return null;
  const lines = selectPacks(rec.amount, rec.unit, prod.packs);
  return lines && lines.length ? { product: prod.name, lines } : null;
}
